import { __awaiter } from '../../_virtual/_tslib.js';
import { Magic } from 'magic-sdk';
import { ethers } from 'ethers';
import { WalletConnectorBase, logger } from '@dynamic-labs/wallet-connector-core';
import { parseEvmNetworks, CancellablePromise, DynamicError } from '@dynamic-labs/utils';
import { MagicClientNetworkHandler } from '../MagicClientNetworkHandler/MagicClientNetworkHandler.js';
import { assignConfirmationScreenToProvider } from '../utils/assignConfirmationScreenToProvider/assignConfirmationScreenToProvider.js';

const storedAddressKey = 'dynamic_magic_address';
class MagicWalletConnector extends WalletConnectorBase {
    constructor(opts) {
        var _a, _b, _c, _d;
        super(opts);
        this.canConnectViaCustodialService = true;
        this.connectedChain = 'EVM';
        this.supportedChains = ['ETH', 'EVM'];
        const apiKey = (_b = (_a = opts.apiProviders) === null || _a === void 0 ? void 0 : _a.magicLink) === null || _b === void 0 ? void 0 : _b.providerProjectId;
        const defaultChainId = (_d = (_c = opts.apiProviders) === null || _c === void 0 ? void 0 : _c.magicLink) === null || _d === void 0 ? void 0 : _d.defaultChainId;
        if (!apiKey) {
            throw new Error('Missing MagicLink configuration. Add your MagicLink API key to your project configuration via the Dynamic Labs dashboard.');
        }
        this._magicClient = new MagicClientNetworkHandler({
            createClient: (config) => new Magic(apiKey, config),
            defaultChainId,
            evmNetworks: opts.evmNetworks,
        });
        this.evmNetworks = parseEvmNetworks(opts.evmNetworks);
        this._walletUiUtils = opts.walletUiUtils;
    }
    getDeepLink() {
        return undefined;
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetchPublicAddress();
        });
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            localStorage.removeItem(storedAddressKey);
            const client = this._magicClient.getClient();
            yield client.user.logout();
        });
    }
    getUserMetadata() {
        return __awaiter(this, void 0, void 0, function* () {
            if (yield this._isLoggedIn()) {
                const client = this._magicClient.getClient();
                return client.user.getMetadata();
            }
            return undefined;
        });
    }
    fetchPublicAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            const address = localStorage.getItem(storedAddressKey);
            if (address)
                return address;
            try {
                this._currentAuthCancellablePromise = CancellablePromise.fromPromise(this.loginWithMagic());
                this._currentAuthCancellablePromise.onCancel(() => {
                    if ('cancelLogIn' in this)
                        this.cancelLogIn();
                });
                yield this._currentAuthCancellablePromise;
                return this.getAndCacheWalletAddress();
            }
            catch (err) {
                // If we don't throw this error, useConnectWallet throws "missing-public-address" later
                // "missing-public-address" displays a UI message we don't need in this scenario
                if (err === null || err === void 0 ? void 0 : err.wasCancelled) {
                    const error = new Error();
                    // Don't show an error message
                    error.code = 'user-cancelled';
                    throw error;
                }
                logger.error(err);
                return;
            }
        });
    }
    getBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            const isLoggedIn = yield this._isLoggedIn();
            if (!isLoggedIn)
                return undefined;
            const provider = yield this.getWeb3Provider();
            if (!provider)
                throw new DynamicError('No provider');
            const signer = provider.getSigner();
            const userAddress = yield signer.getAddress();
            return ethers.utils.formatEther(yield provider.getBalance(userAddress));
        });
    }
    getConnectedAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            const address = localStorage.getItem(storedAddressKey);
            if (!address)
                return [];
            return [address];
        });
    }
    getNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            return this._magicClient.networkId;
        });
    }
    getRpcProvider() {
        return __awaiter(this, void 0, void 0, function* () {
            return undefined;
        });
    }
    getSigner() {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = yield this.getWeb3Provider();
            return provider.getSigner();
        });
    }
    getWeb3Provider() {
        return __awaiter(this, void 0, void 0, function* () {
            const client = this._magicClient.getClient();
            const provider = new ethers.providers.Web3Provider(client.rpcProvider);
            assignConfirmationScreenToProvider(this, provider, this._walletUiUtils);
            return provider;
        });
    }
    setupEventListeners(listeners) {
        this._listeners = listeners;
        this._magicClient.setListeners(listeners);
        this._checkIfLoggedIn();
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = yield this.getWeb3Provider();
            return provider.getSigner().signMessage(messageToSign);
        });
    }
    supportsNetworkSwitching() {
        return true;
    }
    switchNetwork({ networkChainId, }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!networkChainId) {
                return;
            }
            this._magicClient.selectNetwork(networkChainId);
        });
    }
    teardownEventListeners() {
        this._listeners = undefined;
        this._magicClient.setListeners(undefined);
    }
    _isLoggedIn() {
        return __awaiter(this, void 0, void 0, function* () {
            const client = this._magicClient.getClient();
            return client.user.isLoggedIn();
        });
    }
    _checkIfLoggedIn() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const isLoggedIn = yield this._isLoggedIn();
            if (!isLoggedIn) {
                localStorage.removeItem(storedAddressKey);
                (_b = (_a = this._listeners) === null || _a === void 0 ? void 0 : _a.onDisconnect) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
            return isLoggedIn;
        });
    }
    getAndCacheWalletAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            const client = this._magicClient.getClient();
            const metadata = yield client.user.getMetadata();
            if (!metadata || !metadata.publicAddress) {
                throw new Error('No public address');
            }
            if (metadata.issuer) {
                this.providerResources = [`magic-auth:issuer:${metadata.issuer}`];
            }
            localStorage.setItem(storedAddressKey, metadata.publicAddress);
            return metadata.publicAddress;
        });
    }
}

export { MagicWalletConnector, storedAddressKey };
